import { Button, Dropdown, Space } from 'antd';
import type { MenuProps } from 'antd';
import { CloseCircleOutlined, MoreOutlined, RedoOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';
import { businessDateInTimeZone, INVOICE_ACTION, type CobroClienteResponse } from '@aid/shared';
import { getFacturaActionMenu } from '../lib/factura-table-helpers';

export interface FacturasTableActionsProps {
  factura: CobroClienteResponse;
  /** COBROS_ANULAR permission (no new permission); gates the annul entry. */
  canAnular: boolean;
  /** COBROS_ANULAR permission (no new permission); gates the reissue entry. */
  canReissue: boolean;
  onDetail: (id: number) => void;
  onAnular: (factura: CobroClienteResponse) => void;
  onReemitir: (factura: CobroClienteResponse) => void;
}

export function FacturasTableActions({
  factura,
  canAnular,
  canReissue,
  onDetail,
  onAnular,
  onReemitir,
}: FacturasTableActionsProps) {
  const t = useT();
  // Display-only re-issue gate (spec IR-W1 FE); the backend re-checks the
  // window/package authoritatively on preview and on the atomic command.
  const menu = getFacturaActionMenu(factura, { fechaBase: businessDateInTimeZone() });
  const secondaryActions: MenuProps['items'] = [];

  // R2: invoice corrections only use reissue or annulment — the edit entry
  // was removed; `menu` no longer contains an EDIT item.
  const annul = menu.find((item) => item.key === INVOICE_ACTION.ANNUL);
  if (canAnular && annul?.visible) {
    secondaryActions.push({
      key: INVOICE_ACTION.ANNUL,
      label: t('pages.cobros.anular'),
      icon: <CloseCircleOutlined aria-hidden="true" />,
      disabled: !annul.enabled,
    });
  }

  const reissue = menu.find((item) => item.key === INVOICE_ACTION.REISSUE);
  if (canReissue && reissue?.visible) {
    secondaryActions.push({
      key: INVOICE_ACTION.REISSUE,
      label: t('pages.cobros.reemitir'),
      icon: <RedoOutlined aria-hidden="true" />,
    });
  }

  const handleSecondaryAction: MenuProps['onClick'] = ({ key }) => {
    if (key === INVOICE_ACTION.ANNUL) onAnular(factura);
    if (key === INVOICE_ACTION.REISSUE) onReemitir(factura);
  };

  return (
    <Space size="middle" wrap={false}>
      <Button
        type="link"
        aria-label={t('common.ver')}
        onClick={() => onDetail(factura.id)}
        style={{ paddingInline: 0 }}
      >
        {t('common.ver')}
      </Button>
      {secondaryActions.length > 0 && (
        <Dropdown
          menu={{ items: secondaryActions, onClick: handleSecondaryAction }}
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            aria-label={t('pages.cobros.moreActions')}
            title={t('pages.cobros.moreActions')}
          />
        </Dropdown>
      )}
    </Space>
  );
}
